import type { StreamPathCameraConfig } from "lib/navigation/projectPlannedPathToVideo";

/** Front-camera ground-plane projection defaults for Nav2 path overlay on the video feed. */
export const STREAM_PLANNED_PATH_COLOR = "#3b82f6";

function parseEnvFloat(name: string, fallback: number): number {
    const raw = process.env[name];
    if (raw == null || raw === "") {
        return fallback;
    }
    const value = Number.parseFloat(raw);
    return Number.isFinite(value) ? value : fallback;
}

/** Default horizontal FOV (deg, full angle). */
const DEFAULT_HFOV_DEG = 90;
/** Default vertical FOV (deg, full angle). Drives fy via image height (not fx). */
const DEFAULT_VFOV_DEG = 39;

export type StreamPathOverlayPreferences = {
    heightM: number;
    pitchDeg: number;
    hfovDeg: number;
    vfovDeg: number;
    forwardOffsetM: number;
    pathColor: string;
    lineWidth: number;
    opacity: number;
};

export const DEFAULT_STREAM_PATH_OVERLAY_PREFS: StreamPathOverlayPreferences = {
    heightM: parseEnvFloat("REACT_APP_STREAM_PATH_CAMERA_HEIGHT_M", 0.25),
    pitchDeg: parseEnvFloat("REACT_APP_STREAM_PATH_CAMERA_PITCH_DEG", 0),
    hfovDeg: parseEnvFloat("REACT_APP_STREAM_PATH_HFOV_DEG", DEFAULT_HFOV_DEG),
    vfovDeg: parseEnvFloat("REACT_APP_STREAM_PATH_VFOV_DEG", DEFAULT_VFOV_DEG),
    forwardOffsetM: parseEnvFloat("REACT_APP_STREAM_PATH_CAMERA_FORWARD_M", -0.1),
    pathColor: STREAM_PLANNED_PATH_COLOR,
    lineWidth: 10,
    opacity: 0.9,
};

export const STREAM_PATH_OVERLAY_SLIDER_LIMITS = {
    heightM: { min: 0.1, max: 1.5, step: 0.05 },
    pitchDeg: { min: 0, max: 45, step: 0.5 },
    hfovDeg: { min: 40, max: 140, step: 1 },
    vfovDeg: { min: 25, max: 140, step: 1 },
    forwardOffsetM: { min: -0.3, max: 0.5, step: 0.01 },
    lineWidth: { min: 1, max: 12, step: 0.5 },
    opacity: { min: 0.2, max: 1, step: 0.05 },
} as const;

export function streamPathOverlayPrefsToCameraConfig(
    prefs: StreamPathOverlayPreferences,
): StreamPathCameraConfig {
    return {
        heightM: prefs.heightM,
        pitchRad: (prefs.pitchDeg * Math.PI) / 180,
        hfovDeg: prefs.hfovDeg,
        vfovDeg: prefs.vfovDeg,
        forwardOffsetM: prefs.forwardOffsetM,
        minForwardM: 0.15,
        maxForwardM: 25,
    };
}

/** @deprecated Use streamPathOverlayPrefsToCameraConfig with accessibility preferences. */
export const STREAM_PATH_CAMERA_CONFIG = streamPathOverlayPrefsToCameraConfig(
    DEFAULT_STREAM_PATH_OVERLAY_PREFS,
);
